import { formatDate } from '@angular/common';
import { LINE_ITEM_DESCRIPTION_MAX_LENGTH } from '@constants/constants';
import {
  enumHtsChatCarriedFrom,
  enumHtsChatMessageRole,
  enumHtsChatScope,
  enumHtsChatStatus,
  enumHtsVerificationSource,
} from '@data-layer/enums/common';
import {
  HtsChatCarriedModel,
  HtsChatConversationModel,
  HtsChatMessageModel,
  HtsTariffLevelModel,
  SimilarHtsSuggestionModel,
} from '@data-layer/models/common';
import { FavoriteHtsNumberModel } from '@data-layer/models/common/favorite-hts-number.model';
import { UtilsHelper } from '@helpers/utils-helper';
import { HTS_ASSISTANT_CODE_GROUP_LENGTHS } from '../const/hts-assistant.const';
import { enumHtsAssistantLock, enumHtsAssistantMode, enumHtsAssistantThreadItemKind } from '../enums';
import { IHtsAssistantOpenerTexts, IHtsAssistantState, IHtsAssistantThreadItem, IHtsAssistantViewModel } from '../interfaces';

export interface IHtsCodeGroup {
  value: string;
  showDot: boolean;
}

/** Date of a carried conversation in its note. */
const NOTE_DATE_FORMAT: string = 'MM/dd/yyyy';

/** Pure functions behind the classification assistant drawer (#13383). No Angular, no side effects. */
export class HtsAssistantHelper {
  /**
   * Catalog mode (#13554), Start over: the conversation and everything tied to it go away, the drawer is back to its call to
   * action. The company and its catalog codes (a code saved just now is "already in the catalog") stay.
   */
  public static clearedConversation(): Partial<IHtsAssistantState> {
    return {
      conversations: [],
      activeId: null,
      pendingUserMessage: null,
      autoStartedIds: [],
      notice: null,
      insertedIds: [],
      savedIds: [],
      insertingId: null,
      savingId: null,
    };
  }

  /** The state of a drawer before `init`: live importer mode, nothing known about the line. */
  public static initialState(): IHtsAssistantState {
    return {
      mode: enumHtsAssistantMode.Live,
      scope: enumHtsChatScope.Importer,
      lineId: null,
      invoiceId: null,
      companyId: null,
      lineDescription: null,
      draftDescription: null,
      currentHts: null,
      verificationSource: enumHtsVerificationSource.None,
      verificationMatchedText: null,
      verificationReasonText: null,
      currentCodeTariff: null,
      openerTexts: { withDescription: '', catalog: '' },
      conversations: [],
      activeId: null,
      pendingUserMessage: null,
      autoStartedIds: [],
      isLoading: false,
      isBusy: false,
      notice: null,
      insertedIds: [],
      savedIds: [],
      insertingId: null,
      savingId: null,
      catalogHtsDigits: [],
      historyFinalConversationId: null,
      isShowDoOver: false,
      suggestions: [],
      isSuggestionsLoading: false,
      isSuggestionsDismissed: false,
      isSuggestionsShown: false,
      confirmingHts: null,
      isHostHeld: false,
      isCatalogMode: false,
    };
  }

  /** "9403.89.6003" → "9403896003". */
  public static toDigits(value: string | null | undefined): string {
    return (value || '').replace(/\D/g, '');
  }

  /** "8544422000" → "8544.42.2000" (the app's mask); shorter codes keep the dots they reach ("854442" → "8544.42", "85" → "85"). */
  public static formatHtsNumber(value: string | null | undefined): string {
    const digits: string = HtsAssistantHelper.toDigits(value);
    let offset: number = 0;
    return HTS_ASSISTANT_CODE_GROUP_LENGTHS.map((length: number) => {
      const group: string = digits.slice(offset, offset + length);
      offset += length;
      return group;
    })
      .filter((group: string) => !!group)
      .join('.');
  }

  /**
   * The dotted groups of the app's `0000.00.0000` mask, the way the catalog cards show a code: 9403896003 → 9403 . 89 . 6003
   * (#13533). A shorter code keeps its three boxes: a group without digits is '' and the code box shows placeholders.
   */
  public static toCodeGroups(htsNumber: string | null | undefined): IHtsCodeGroup[] {
    const digits: string = HtsAssistantHelper.toDigits(htsNumber);
    let offset: number = 0;
    return HTS_ASSISTANT_CODE_GROUP_LENGTHS.map((length: number, index: number) => {
      const group: string = digits.slice(offset, offset + length);
      offset += length;
      return { value: group, showDot: index < HTS_ASSISTANT_CODE_GROUP_LENGTHS.length - 1 };
    });
  }

  /**
   * The tariff description of a code as the backend reports it level by level (chapter → … → code, #13513), the way the
   * Bina catalog shows its (backend-concatenated) descriptions: every level's text in one paragraph, codes left out.
   */
  public static joinTariffLevels(levels: HtsTariffLevelModel[] | null | undefined): string {
    return (levels || [])
      .filter((level: HtsTariffLevelModel) => !!level && !!level.description)
      .map((level: HtsTariffLevelModel) => level.description.trim())
      .filter((text: string) => !!text)
      .join(' ');
  }

  /**
   * Lists that show catalog items next to similar-product suggestions (#13533): a suggestion whose code is already on a
   * catalog card is left out — the catalog card wins (product decision: Bina Catalog has priority over the same code).
   */
  public static withoutCatalogCodes(
    suggestions: SimilarHtsSuggestionModel[] | null | undefined,
    catalogItems: FavoriteHtsNumberModel[] | null | undefined,
  ): SimilarHtsSuggestionModel[] {
    const catalogDigits: Set<string> = new Set(
      (catalogItems || []).map((item: FavoriteHtsNumberModel) => HtsAssistantHelper.toDigits(item.htsNumber)),
    );
    return (suggestions || []).filter(
      (suggestion: SimilarHtsSuggestionModel) => !catalogDigits.has(HtsAssistantHelper.toDigits(suggestion.htsNumber)),
    );
  }

  /** What a chat description becomes on the line: normalized and cut to the line item cap (the form validates the same cap). */
  public static toLineDescription(value: string | null | undefined): string {
    return UtilsHelper.limitLength(HtsAssistantHelper.normalizeDescription(value), LINE_ITEM_DESCRIPTION_MAX_LENGTH, false);
  }

  public static normalizeDescription(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
  }

  public static formatOpener(texts: IHtsAssistantOpenerTexts, description: string | null): string {
    return texts.withDescription.replace('{{description}}', (description || '').trim());
  }

  /** Oldest first; equal start dates fall back to the id so the order never depends on the backend's list order. */
  public static sortByStartedDate(conversations: HtsChatConversationModel[]): HtsChatConversationModel[] {
    return [...conversations].sort(
      (a: HtsChatConversationModel, b: HtsChatConversationModel) =>
        new Date(a.startedDate).getTime() - new Date(b.startedDate).getTime() || a.id - b.id,
    );
  }

  public static upsert(conversations: HtsChatConversationModel[], conversation: HtsChatConversationModel): HtsChatConversationModel[] {
    const isKnown: boolean = conversations.some((c: HtsChatConversationModel) => c.id === conversation.id);
    return isKnown
      ? conversations.map((c: HtsChatConversationModel) => (c.id === conversation.id ? conversation : c))
      : [...conversations, conversation];
  }

  /** The conversation the composer talks to: the explicitly selected one, else the backend's active one. */
  public static findActive(state: IHtsAssistantState): HtsChatConversationModel | null {
    if (state.activeId !== null) {
      const byId: HtsChatConversationModel = state.conversations.find((c: HtsChatConversationModel) => c.id === state.activeId);
      if (byId) {
        return byId;
      }
    }
    return state.conversations.find((c: HtsChatConversationModel) => c.isActive) || null;
  }

  public static isCompleted(conversation: HtsChatConversationModel | null): boolean {
    return !!conversation && conversation.status === enumHtsChatStatus.Completed && !!conversation.result;
  }

  /** Finished without a code (status NoAnswer, a normal outcome): nothing to insert or save, only Start over. */
  public static isNoAnswer(conversation: HtsChatConversationModel | null): boolean {
    return !!conversation && conversation.status === enumHtsChatStatus.NoAnswer;
  }

  public static isAwaitingAnswer(conversation: HtsChatConversationModel | null): boolean {
    return !!conversation && conversation.isActive && conversation.status === enumHtsChatStatus.AwaitingAnswer;
  }

  public static buildViewModel(state: IHtsAssistantState): IHtsAssistantViewModel {
    const isReadonly: boolean = state.mode === enumHtsAssistantMode.Readonly;
    const allConversations: HtsChatConversationModel[] = HtsAssistantHelper.sortByStartedDate(state.conversations);
    // Do-over history (admin, Broker Review): only the final conversation until the toggle reveals the superseded ones (dimmed).
    const finalId: number | null = isReadonly ? state.historyFinalConversationId : null;
    const finalConversation: HtsChatConversationModel | null =
      finalId !== null ? allConversations.find((c: HtsChatConversationModel) => c.id === finalId) || null : null;
    const conversations: HtsChatConversationModel[] = HtsAssistantHelper.orderForThread(
      allConversations,
      finalConversation,
      state.isShowDoOver,
    );
    // a catalog item's history (#13554): no line verification names the final conversation, and `isActive` tells nothing
    // (a conversation dropped by Start over stays active on the backend) — the latest conversation is the one that counts
    const latest: HtsChatConversationModel | null = allConversations.length > 0 ? allConversations[allConversations.length - 1] : null;
    const active: HtsChatConversationModel | null =
      finalConversation || (isReadonly && state.isCatalogMode ? latest : HtsAssistantHelper.findActive(state));
    const isCompleted: boolean = HtsAssistantHelper.isCompleted(active);
    const isNoAnswer: boolean = HtsAssistantHelper.isNoAnswer(active);
    const isFinished: boolean = isCompleted || isNoAnswer;
    // "ended without a result" excludes NoAnswer: that one carries its own note and Start over (no second Restart item)
    const isEnded: boolean = !!active && !active.isActive && !active.result && !isNoAnswer;
    const lockReason: enumHtsAssistantLock | null = isReadonly ? null : HtsAssistantHelper.lockReason(state);
    const isLoading: boolean = state.isLoading || state.isSuggestionsLoading;
    const isSuggestionsStep: boolean = !isReadonly && !isLoading && HtsAssistantHelper.isSuggestionsPending(state);
    const isSuggestionsToggle: boolean =
      !isReadonly && !isSuggestionsStep && state.isSuggestionsDismissed && HtsAssistantHelper.hasSuggestionsToOffer(state);
    // a verified line's own code goes first; the backend list is shown without it
    const currentDigits: string = HtsAssistantHelper.currentCodeDigits(state);
    const suggestions: SimilarHtsSuggestionModel[] = currentDigits
      ? state.suggestions.filter((s: SimilarHtsSuggestionModel) => HtsAssistantHelper.toDigits(s.htsNumber) !== currentDigits)
      : state.suggestions;
    const isShowStartOver: boolean = !isReadonly && (isFinished || isEnded) && !lockReason && !state.isBusy;
    // a completed conversation offers Start over on its result card; one that ended WITHOUT a result has no card -> note + button
    const restartNoteKey: string | null = isShowStartOver && isEnded ? 'HTS_ASSISTANT.ENDED_NOTE' : null;
    const threadItems: IHtsAssistantThreadItem[] = HtsAssistantHelper.dimSuperseded(
      HtsAssistantHelper.buildThread(state, conversations, active, isReadonly, restartNoteKey, isShowStartOver),
      finalConversation && state.isShowDoOver ? finalConversation.id : null,
    );

    return {
      description: active ? active.description : state.lineDescription,
      threadItems,
      messagesCount: allConversations.reduce((sum: number, c: HtsChatConversationModel) => sum + (c.messages || []).length, 0),
      isCatalogStart: state.isCatalogMode && !HtsAssistantHelper.isAwaitingAnswer(active),
      isLoading,
      isBusy: state.isBusy,
      isCompleted,
      isNoAnswer,
      isEnded,
      isReadonly,
      lockReason,
      isShowComposer: !isReadonly && !isFinished && !isEnded && !isLoading && !isSuggestionsStep,
      isShowStartOver,
      isEmptyReadonly: isReadonly && !allConversations.length && !state.isLoading,
      isDoOverAvailable: !!finalConversation && allConversations.length > 1,
      isShowDoOver: state.isShowDoOver,
      notice: state.notice,
      currentSuggestion: currentDigits ? HtsAssistantHelper.currentSuggestion(state, currentDigits) : null,
      suggestions,
      isSuggestionsStep,
      isSuggestionsToggle,
      isSuggestionsOverlay: isSuggestionsToggle && state.isSuggestionsShown,
      confirmingHts: state.confirmingHts,
      verificationSource: state.verificationSource,
      verificationMatchedText: state.verificationMatchedText,
      currentHts: state.currentHts,
    };
  }

  /**
   * The suggestions step is on the table and was not dismissed: similar products the company classified before (#13517)
   * and / or the code a verified line already holds (demo rework 2026-10-16). No conversation starts until the user decides.
   */
  public static isSuggestionsPending(state: IHtsAssistantState): boolean {
    return !state.isSuggestionsDismissed && !HtsAssistantHelper.findActive(state) && HtsAssistantHelper.hasSuggestionsToOffer(state);
  }

  /** Live importer mode: the only mode that acts for the user on the backend (starts, inserts, silent lookups). */
  public static isLiveImporter(state: IHtsAssistantState): boolean {
    return state.mode === enumHtsAssistantMode.Live && state.scope === enumHtsChatScope.Importer;
  }

  /** Something to show on the suggestions step: backend suggestions, or the current code of a verified line. */
  public static hasSuggestionsToOffer(state: IHtsAssistantState): boolean {
    return state.suggestions.length > 0 || !!HtsAssistantHelper.currentCodeDigits(state);
  }

  /** Digits of the code a VERIFIED line holds; '' on an unverified line (a code typed by hand is not "current"). */
  public static currentCodeDigits(state: IHtsAssistantState): string {
    return state.verificationSource !== enumHtsVerificationSource.None ? HtsAssistantHelper.toDigits(state.currentHts) : '';
  }

  /**
   * A verified line (any source but `None`) that holds no active conversation: nothing to resume, nothing to start on its
   * own — the user may still open a conversation deliberately (product decision 2026-10-12, N7).
   */
  public static isVerifiedWithoutChat(state: IHtsAssistantState): boolean {
    return state.verificationSource !== enumHtsVerificationSource.None && !HtsAssistantHelper.findActive(state);
  }

  /**
   * The chat classifies the SAVED description only (#13384 F15): no description → nothing to start; standalone host with a
   * description typed but not saved → wait for Find HTS (which saves the line and reopens the drawer). A verified line
   * without an active conversation normally gets the suggestions step (its code first); with nothing to show there (no
   * code, no suggestions) it waits for an explicit "Start a conversation" (not while a start or a lookup is in flight).
   */
  private static lockReason(state: IHtsAssistantState): enumHtsAssistantLock | null {
    // catalog mode (#13554): no line and no saved description — the first message is the description, nothing to wait for
    if (state.isCatalogMode) {
      return null;
    }
    const saved: string = HtsAssistantHelper.normalizeDescription(state.lineDescription);
    if (!saved) {
      return enumHtsAssistantLock.NoDescription;
    }
    if (state.draftDescription !== null && HtsAssistantHelper.normalizeDescription(state.draftDescription) !== saved) {
      return enumHtsAssistantLock.DraftChanged;
    }
    const isIdle: boolean = !state.isBusy && !state.isLoading && !state.isSuggestionsLoading;
    if (isIdle && HtsAssistantHelper.isVerifiedWithoutChat(state) && !HtsAssistantHelper.hasSuggestionsToOffer(state)) {
      return state.verificationSource === enumHtsVerificationSource.AssistantNoAnswer
        ? enumHtsAssistantLock.NoAnswerEarlier
        : enumHtsAssistantLock.Validated;
    }
    return null;
  }

  /**
   * The card of the code a verified line holds: the backend's own suggestion for that code (title, tariff path) when it
   * is in the list, else a client-built one titled with the verification reason and the tariff path the hts-chat read
   * reported for that code (#13550) — no description when the code is unknown to the tariff or changed since the read.
   */
  private static currentSuggestion(state: IHtsAssistantState, digits: string): SimilarHtsSuggestionModel {
    const known: SimilarHtsSuggestionModel | undefined = state.suggestions.find(
      (s: SimilarHtsSuggestionModel) => HtsAssistantHelper.toDigits(s.htsNumber) === digits,
    );
    if (known) {
      return known;
    }
    const isTariffKnown: boolean = !!state.currentCodeTariff && state.currentCodeTariff.digits === digits;
    return {
      htsNumber: digits,
      title: state.verificationReasonText,
      tariffDescription: isTariffKnown ? state.currentCodeTariff.levels : [],
      matchedText: '',
      source: null,
      conversationId: null,
      sourceLineId: null,
      catalogItemId: null,
      similarity: 0,
    };
  }

  /**
   * Do-over history (#13548): the final conversation alone until revealed; revealed, the superseded attempts go above in
   * start order and the final one stays last — the applied one may be older than a later attempt that was never applied.
   */
  private static orderForThread(
    all: HtsChatConversationModel[],
    finalConversation: HtsChatConversationModel | null,
    isShowDoOver: boolean,
  ): HtsChatConversationModel[] {
    if (!finalConversation) {
      return all;
    }
    if (!isShowDoOver) {
      return [finalConversation];
    }
    return [...all.filter((c: HtsChatConversationModel) => c.id !== finalConversation.id), finalConversation];
  }

  /** Every item of a conversation other than the final one is dimmed; item keys carry the conversation id after the kind. */
  private static dimSuperseded(items: IHtsAssistantThreadItem[], finalId: number | null): IHtsAssistantThreadItem[] {
    if (finalId === null) {
      return items;
    }
    return items.map((item: IHtsAssistantThreadItem) => {
      const match: RegExpMatchArray | null = item.key.match(/^[a-z]+-(\d+)/);
      const conversationId: number | null = item.conversationId !== undefined ? item.conversationId : match ? Number(match[1]) : null;
      return conversationId !== null && conversationId !== finalId ? { ...item, isDimmed: true } : item;
    });
  }

  private static buildThread(
    state: IHtsAssistantState,
    conversations: HtsChatConversationModel[],
    active: HtsChatConversationModel | null,
    isReadonly: boolean,
    restartNoteKey: string | null,
    isStartOverAvailable: boolean,
  ): IHtsAssistantThreadItem[] {
    const items: IHtsAssistantThreadItem[] = [];

    // live catalog mode (#13554): no description to quote — the local call to action asks for one and heads the thread for
    // good (a Start over clears the thread, so the opener is the only item again and never more than one conversation follows
    // it); a catalog item's read-only history has nothing to ask for
    if (state.isCatalogMode && !isReadonly) {
      items.push({
        key: 'opener-catalog',
        kind: enumHtsAssistantThreadItemKind.Message,
        role: enumHtsChatMessageRole.Classifier,
        content: state.openerTexts.catalog,
      });
    }

    conversations.forEach((conversation: HtsChatConversationModel, index: number) => {
      if (index > 0) {
        items.push({ key: `sep-${conversation.id}`, kind: enumHtsAssistantThreadItemKind.Separator });
      }
      // messages[0] equal to the line's stored description is its echo, hidden ONLY when this client started the
      // conversation from that description (auto-start): a description typed in the chat (legacy flow) stays a bubble.
      const isLineDescriptionEcho: boolean = HtsAssistantHelper.isLineDescriptionEcho(conversation, state.lineDescription);
      const isEchoHidden: boolean = isLineDescriptionEcho && state.autoStartedIds.includes(conversation.id);

      // history (#13385): a conversation held elsewhere (catalog, duplicated / earlier entry, similar product) says so first
      if (conversation.carried) {
        items.push({
          key: `carried-${conversation.id}`,
          kind: enumHtsAssistantThreadItemKind.Note,
          isCarried: true,
          conversationId: conversation.id,
          content: HtsAssistantHelper.carriedKey(conversation.carried),
          params: {
            entry: HtsAssistantHelper.carriedEntry(conversation.carried),
            date: conversation.startedDate ? formatDate(conversation.startedDate, NOTE_DATE_FORMAT, 'en-US') : '',
            text: state.verificationMatchedText || '',
          },
        });
      }

      const messages: HtsChatMessageModel[] = HtsAssistantHelper.sortedMessages(conversation).slice(isEchoHidden ? 1 : 0);
      messages.forEach((message: HtsChatMessageModel) => {
        items.push({
          key: `msg-${conversation.id}-${message.sequence}`,
          kind: enumHtsAssistantThreadItemKind.Message,
          role: message.role,
          content: message.content,
          conversationId: conversation.id,
        });
      });

      if (HtsAssistantHelper.hasPendingQuestionToShow(conversation, messages)) {
        items.push({
          key: `pending-${conversation.id}`,
          kind: enumHtsAssistantThreadItemKind.Message,
          role: enumHtsChatMessageRole.Classifier,
          content: conversation.pendingQuestion,
          conversationId: conversation.id,
        });
      }

      // history: a conversation still waiting for an answer is told apart from a finished one
      if (isReadonly && conversation.status === enumHtsChatStatus.AwaitingAnswer) {
        items.push({
          key: `unfinished-${conversation.id}`,
          kind: enumHtsAssistantThreadItemKind.Note,
          conversationId: conversation.id,
          content: 'HTS_ASSISTANT.UNFINISHED_NOTE',
        });
      }

      if (HtsAssistantHelper.isCompleted(conversation)) {
        const digits: string = HtsAssistantHelper.toDigits(conversation.result.htsNumber);
        items.push({
          key: `result-${conversation.id}`,
          kind: enumHtsAssistantThreadItemKind.Result,
          conversationId: conversation.id,
          result: conversation.result,
          isStale: !active || active.id !== conversation.id,
          // a code typed by hand that equals the result is not "inserted" until the line is verified: Insert still verifies it
          isInserted:
            state.insertedIds.includes(conversation.id) ||
            (!!digits &&
              digits === HtsAssistantHelper.toDigits(state.currentHts) &&
              state.verificationSource !== enumHtsVerificationSource.None),
          // saved in this drawer, or already saved earlier (the backend reports the catalog item on the conversation)
          isSaved: state.savedIds.includes(conversation.id) || !!conversation.catalogItemId,
          isInCatalog: !!digits && state.catalogHtsDigits.includes(digits),
          isInserting: state.insertingId === conversation.id,
          isSaving: state.savingId === conversation.id,
          isStartOverAvailable: isStartOverAvailable && !!active && active.id === conversation.id,
        });
      }

      if (HtsAssistantHelper.isNoAnswer(conversation)) {
        items.push({
          key: `no-answer-${conversation.id}`,
          kind: enumHtsAssistantThreadItemKind.NoAnswer,
          conversationId: conversation.id,
          isStale: !active || active.id !== conversation.id,
          isStartOverAvailable: isStartOverAvailable && !!active && active.id === conversation.id,
        });
      }

      // ended without a result: nowhere else to offer Start over, so the note and the button sit under the last message
      if (restartNoteKey && active && active.id === conversation.id) {
        items.push({ key: `restart-${conversation.id}`, kind: enumHtsAssistantThreadItemKind.Restart, content: restartNoteKey });
      }
    });

    // Nothing started and nothing in flight (an auto-start failed): the local opener quotes the saved description so the
    // user can still type and start the conversation by hand. Without a description the lock card explains instead
    // (catalog mode has no line description, so its opener above is the only one).
    const isLoading: boolean = state.isLoading || state.isSuggestionsLoading;
    if (!isReadonly && !conversations.length && !isLoading && !state.isBusy && !!state.lineDescription) {
      items.push(HtsAssistantHelper.openerItem('opener-new', state.openerTexts, state.lineDescription));
    }

    if (state.pendingUserMessage) {
      items.push({
        key: 'pending-user',
        kind: enumHtsAssistantThreadItemKind.Message,
        role: enumHtsChatMessageRole.User,
        content: state.pendingUserMessage,
      });
    }

    if (state.isBusy) {
      items.push({ key: 'thinking', kind: enumHtsAssistantThreadItemKind.Thinking });
    }

    return items;
  }

  private static carriedKey(carried: HtsChatCarriedModel): string {
    switch (carried.from) {
      case enumHtsChatCarriedFrom.Catalog:
        return 'HTS_ASSISTANT.CARRIED_CATALOG';
      case enumHtsChatCarriedFrom.DuplicatedEntry:
        return 'HTS_ASSISTANT.CARRIED_DUPLICATED';
      case enumHtsChatCarriedFrom.CompanyHistory:
        return 'HTS_ASSISTANT.CARRIED_REUSED';
      case enumHtsChatCarriedFrom.SimilarProduct:
        return 'HTS_ASSISTANT.CARRIED_SIMILAR';
      default:
        return 'HTS_ASSISTANT.CARRIED_ELSEWHERE';
    }
  }

  private static carriedEntry(carried: HtsChatCarriedModel): string {
    if (carried.entrySummaryReferenceNumber) {
      return carried.entrySummaryReferenceNumber;
    }
    return carried.entrySummaryId !== null && carried.entrySummaryId !== undefined ? `#${carried.entrySummaryId}` : '';
  }

  private static openerItem(key: string, texts: IHtsAssistantOpenerTexts, description: string | null): IHtsAssistantThreadItem {
    return {
      key,
      kind: enumHtsAssistantThreadItemKind.Message,
      role: enumHtsChatMessageRole.Classifier,
      content: HtsAssistantHelper.formatOpener(texts, description),
    };
  }

  private static sortedMessages(conversation: HtsChatConversationModel): HtsChatMessageModel[] {
    return [...(conversation.messages || [])].sort((a: HtsChatMessageModel, b: HtsChatMessageModel) => a.sequence - b.sequence);
  }

  /** messages[0] is the line's description sent as the opening message (not something the user typed in the chat). */
  private static isLineDescriptionEcho(conversation: HtsChatConversationModel, lineDescription: string | null): boolean {
    const line: string = HtsAssistantHelper.normalizeDescription(lineDescription);
    const first: HtsChatMessageModel = HtsAssistantHelper.sortedMessages(conversation)[0];
    if (!line || !first || first.role !== enumHtsChatMessageRole.User) {
      return false;
    }
    // the line may hold the cut-to-cap version of a longer chat description (see `toLineDescription`)
    return HtsAssistantHelper.normalizeDescription(first.content) === line || HtsAssistantHelper.toLineDescription(first.content) === line;
  }

  private static hasPendingQuestionToShow(conversation: HtsChatConversationModel, messages: HtsChatMessageModel[]): boolean {
    if (!HtsAssistantHelper.isAwaitingAnswer(conversation) || !conversation.pendingQuestion) {
      return false;
    }
    const last: HtsChatMessageModel = messages[messages.length - 1];
    return !(last && last.role === enumHtsChatMessageRole.Classifier && last.content === conversation.pendingQuestion);
  }
}
